import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isToday, isThisWeek, parseISO } from "date-fns";
import { History, Pin, PinOff, Plus, Search, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useApi, useInvalidate } from "@/hooks/use-api";
import { useWorkspaceChat } from "@/hooks/use-workspace-chat";
import { api } from "@/lib/api";
import { Thread } from "@/components/ai/Thread";
import { Composer } from "@/components/ai/Composer";
import { ArtifactCard } from "@/components/ai/Artifacts";
import { LensMark } from "@/components/brand/ProximaLogo";
import { fmtRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

const groupLabel = (iso) => {
  const d = parseISO(iso);
  if (isToday(d)) return "I dag";
  if (isThisWeek(d, { weekStartsOn: 1 })) return "Denne uken";
  return "Tidligere";
};

/* ---------------- History rail ---------------- */
const ConversationList = ({ conversations = [], activeId, onSelect, onNew, loading, query, setQuery, onPin, onDelete }) => {
  const groups = useMemo(() => {
    const pinned = conversations.filter((c) => c.pinned);
    const rest = conversations.filter((c) => !c.pinned);
    const out = [];
    if (pinned.length) out.push(["Festet", pinned]);
    const by = {};
    rest.forEach((c) => {
      const g = groupLabel(c.updated_at);
      (by[g] = by[g] || []).push(c);
    });
    ["I dag", "Denne uken", "Tidligere"].forEach((g) => by[g] && out.push([g, by[g]]));
    return out;
  }, [conversations]);
  return (
    <div className="flex h-full flex-col" data-testid="ai-history-rail">
      <div className="space-y-2 p-3">
        <Button variant="outline" className="h-9 w-full justify-start gap-2 px-3 text-[14px]" onClick={onNew} data-testid="ai-new-chat-button">
          <Plus className="h-4 w-4" /> Ny samtale
        </Button>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søk i samtaler" className="h-9 w-full rounded-md bg-rail-hover/70 pl-8 pr-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-ring" data-testid="ai-history-search-input" />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {loading ? (
          <div className="space-y-2 p-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8" />
            ))}
          </div>
        ) : !conversations.length ? (
          <p className="px-2 py-6 text-[13.5px] leading-relaxed text-muted-foreground">Ingen samtaler ennå. Still et spørsmål for å starte.</p>
        ) : (
          groups.map(([g, items]) => (
            <div key={g} className="mb-4">
              <div className="px-2 pb-1.5 pt-1 text-[12.5px] font-medium text-muted-foreground">{g}</div>
              <ul className="space-y-px">
                {items.map((c) => {
                  const active = activeId === c.id;
                  return (
                    <li key={c.id} className="group relative">
                      <button type="button" onClick={() => onSelect(c.id)} title={c.title || "Ny samtale"} className={cn("flex h-8 w-full items-center rounded-md px-2 text-left text-[14px] transition-[background-color,color] duration-100", active ? "bg-card text-foreground shadow-[0_1px_2px_hsl(var(--brand-ink)/0.06),0_0_0_1px_hsl(var(--border)/0.9)]" : "text-foreground/80 hover:bg-rail-hover hover:text-foreground")} data-testid="ai-conversation-list-item">
                        <span className="truncate pr-12">{c.title || "Ny samtale"}</span>
                      </button>
                      <div className={cn("absolute right-1 top-1 hidden items-center gap-0.5 group-hover:flex", active && "flex")}>
                        <Button variant="quiet" size="icon-xs" className="h-6 w-6" onClick={() => onPin(c)} aria-label={c.pinned ? "Løsne" : "Fest"} data-testid="ai-conversation-pin-button">
                          {c.pinned ? <PinOff className="!h-3.5 !w-3.5" /> : <Pin className="!h-3.5 !w-3.5" />}
                        </Button>
                        <Button variant="quiet" size="icon-xs" className="h-6 w-6" onClick={() => onDelete(c)} aria-label="Slett" data-testid="ai-conversation-delete-button">
                          <Trash2 className="!h-3.5 !w-3.5" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/* ---------------- Empty state: greeting + composer + starters ---------------- */
const EmptyThread = ({ starters, onPick, plantName, firstName, composer }) => (
  <div className="flex min-h-full flex-col items-center justify-center px-4 py-10 sm:px-6" data-testid="ai-empty-state">
    <div className="w-full max-w-[720px]">
      <LensMark className="h-8 w-8" />
      <h1 className="mt-5 text-[28px] font-semibold leading-tight tracking-[-0.015em]">{firstName ? `Hva vil du vite, ${firstName}?` : "Hva vil du vite?"}</h1>
      <p className="mt-2 max-w-[600px] text-[15px] leading-relaxed text-muted-foreground">
        Spør om {plantName ? <span className="font-medium text-foreground">{plantName}</span> : "kraftverkene dine"} – produksjon, stans, hendelser, inntekt, vannføring – eller om hva som helst annet. Svar om anleggene bygger på faktiske målinger og hendelser, og vises gjerne som graf eller tabell.
      </p>
      <div className="mt-6">{composer}</div>
      <div className="mt-10 grid gap-x-8 gap-y-6 sm:grid-cols-3">
        {(starters || []).map((g) => (
          <div key={g.group}>
            <div className="text-[13px] font-medium text-muted-foreground">{g.group}</div>
            <ul className="mt-1.5">
              {g.items.map((s) => (
                <li key={s}>
                  <button type="button" onClick={() => onPick(s)} className="-mx-2 w-[calc(100%+16px)] rounded-md px-2 py-2 text-left text-[14px] leading-snug text-foreground transition-[background-color,color] duration-100 hover:bg-muted hover:text-primary" data-testid="ai-prompt-starter">
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function AiWorkspacePage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [plantId, setPlantId] = useState(params.get("plant") || "");
  const [pinned, setPinned] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const conversations = useApi("/workspace/conversations", { q: query || undefined }, { staleTime: 10_000 });
  const starters = useApi("/workspace/starters", {}, { staleTime: 600_000 });
  const plants = useApi("/plants", {}, { staleTime: 120_000 });
  const chat = useWorkspaceChat({ initialConversationId: params.get("c") || null });
  const startedFromQuery = useRef(false);

  // ?q= from the command palette / contextual links: start a fresh conversation with that question.
  useEffect(() => {
    const q = params.get("q");
    if (q && !startedFromQuery.current) {
      startedFromQuery.current = true;
      chat.reset();
      chat.send(q, { plantId: params.get("plant") || null }).then(() => {
        const next = new URLSearchParams(params);
        next.delete("q");
        setParams(next, { replace: true });
        invalidate("/workspace/conversations");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chat.sending && chat.messages.length) invalidate("/workspace/conversations");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.sending]);

  // Keep the URL in sync with the conversation (so it survives reload / can be shared internally).
  useEffect(() => {
    if (chat.conversationId && params.get("c") !== chat.conversationId) {
      const next = new URLSearchParams(params);
      next.set("c", chat.conversationId);
      next.delete("q");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.conversationId]);

  const select = (id) => {
    if (id === chat.conversationId) {
      setHistoryOpen(false);
      return;
    }
    chat.load(id);
    setHistoryOpen(false);
    const next = new URLSearchParams(params);
    next.set("c", id);
    setParams(next, { replace: true });
  };
  const fresh = () => {
    chat.reset();
    setPinned([]);
    setHistoryOpen(false);
    navigate("/ai", { replace: true });
  };
  const pinConv = async (c) => {
    await api.patch(`/workspace/conversations/${c.id}`, { pinned: !c.pinned });
    invalidate("/workspace/conversations");
  };
  const deleteConv = async (c) => {
    await api.delete(`/workspace/conversations/${c.id}`);
    toast.success("Samtalen ble slettet");
    if (chat.conversationId === c.id) fresh();
    invalidate("/workspace/conversations");
  };
  const togglePin = (a) => setPinned((prev) => (prev.find((p) => p.id === a.id) ? prev.filter((p) => p.id !== a.id) : [...prev, a]));
  const pinnedIds = pinned.map((p) => p.id);
  const plantList = plants.data?.plants || [];
  const plantName = plantList.find((p) => p.id === plantId)?.name || null;
  const send = (text) => chat.send(text, { plantId: plantId || null });
  const firstName = (user?.name || "").split(" ")[0];
  const empty = !chat.loading && !chat.messages.length;
  const composer = <Composer onSend={send} onStop={chat.stop} sending={chat.sending} contextLabel={plantName} onClearContext={plantId ? () => setPlantId("") : undefined} autoFocus />;

  return (
    <div className="flex h-[calc(100vh-52px)] min-h-0 lg:h-screen" data-testid="ai-workspace-page">
      {/* History rail (desktop) */}
      <aside className="hidden w-[264px] shrink-0 border-r border-border bg-[hsl(var(--rail))] lg:block">
        <ConversationList conversations={conversations.data?.conversations} activeId={chat.conversationId} onSelect={select} onNew={fresh} loading={conversations.isLoading} query={query} setQuery={setQuery} onPin={pinConv} onDelete={deleteConv} />
      </aside>

      {/* Thread column */}
      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 shrink-0 items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <Button variant="quiet" size="icon-sm" className="lg:hidden" onClick={() => setHistoryOpen(true)} aria-label="Samtaler" data-testid="ai-history-open-button">
              <History className="h-4 w-4" />
            </Button>
            <h2 className="truncate text-[14.5px] font-medium text-foreground" data-testid="ai-conversation-title">
              {chat.conversation?.title || (empty ? "Spør HydroHub" : "Ny samtale")}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Select value={plantId || "all"} onValueChange={(v) => setPlantId(v === "all" ? "" : v)}>
              <SelectTrigger className="select-quiet max-w-[240px]" aria-label="Kontekst" data-testid="ai-context-select">
                <SelectValue placeholder="Kontekst" />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="all">Hele porteføljen</SelectItem>
                {plantList.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="hidden whitespace-nowrap text-[12.5px] text-muted-foreground sm:inline" data-testid="ai-model-badge">
              {starters.data?.model || "GPT-5"}
            </span>
          </div>
        </div>

        {empty ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyThread starters={starters.data?.groups} onPick={send} plantName={plantName} firstName={firstName} composer={composer} />
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {chat.loading ? (
                <div className="mx-auto max-w-[760px] space-y-4 px-4 py-8 sm:px-6">
                  <Skeleton className="ml-auto h-10 w-1/2" />
                  <Skeleton className="h-24" />
                  <Skeleton className="h-40" />
                </div>
              ) : (
                <div className="mx-auto max-w-[760px] px-4 pt-4 sm:px-6">
                  <Thread messages={chat.messages} onPin={togglePin} pinnedIds={pinnedIds} />
                  <div className="h-4" />
                </div>
              )}
            </div>
            <div className="shrink-0 px-4 pb-4 pt-2 sm:px-6" style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
              <div className="mx-auto max-w-[760px]">
                {composer}
                <p className="mt-2 text-center text-[12px] text-muted-foreground">Kun lesetilgang · svar om anleggene bygger på fiktive eksempeldata i prototypen</p>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Pinned artifacts dock */}
      {pinned.length ? (
        <aside className="hidden w-[380px] shrink-0 overflow-y-auto border-l border-border bg-[hsl(var(--rail))] p-4 xl:block" data-testid="ai-artifact-dock">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[14px] font-semibold">Festet</h3>
            <Button variant="quiet" size="xs" onClick={() => setPinned([])} data-testid="ai-dock-clear-button">
              <X className="!h-3.5 !w-3.5" /> Tøm
            </Button>
          </div>
          <div className="space-y-3">
            {pinned.map((a) => (
              <ArtifactCard key={a.id} artifact={a} compact pinned onPin={togglePin} />
            ))}
          </div>
        </aside>
      ) : null}

      {/* Mobile history drawer */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="left" className="w-[320px] p-0">
          <SheetHeader className="border-b border-border px-4 py-3">
            <SheetTitle className="text-[15px]">Samtaler</SheetTitle>
          </SheetHeader>
          <div className="h-[calc(100%-49px)]">
            <ConversationList conversations={conversations.data?.conversations} activeId={chat.conversationId} onSelect={select} onNew={fresh} loading={conversations.isLoading} query={query} setQuery={setQuery} onPin={pinConv} onDelete={deleteConv} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
